import Card from "@/components/Card";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { getDataVerifikasiSkl, getMasterSystem } from "@/services/getData";
import { DataBerkas, DataPendaftaran } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaCheck } from "react-icons/fa6";
import { GiBackwardTime } from "react-icons/gi";
import { MdBlockFlipped } from "react-icons/md";
import { Link, useSearchParams } from "react-router-dom";

type ValidationState = {
  id_data_pendaftaran: string;
  id_data_berkas_siswa: string;
  is_valid: boolean;
  catatan_verifikator: string;
};

const patchDataValidasi = async (form: ValidationState) => {
  const response = await api.patch("/verifikator/peserta-skl/validate", form);
  return response.data;
};

const patchDataResetValidasi = async ({
  id_data_pendaftaran,
  id_data_berkas_siswa,
}: {
  id_data_pendaftaran: string;
  id_data_berkas_siswa: string;
}) => {
  const response = await api.patch("/verifikator/peserta-skl/reset", {
    id_data_pendaftaran,
    id_data_berkas_siswa,
  });
  return response.data;
};

export default function VerifikasiSkl() {
  const [searchParams] = useSearchParams();
  const nomorPendaftaranParams = searchParams.get("no") || "";

  const [form, setForm] = useState<ValidationState>({
    id_data_pendaftaran: "",
    id_data_berkas_siswa: "",
    is_valid: false,
    catatan_verifikator: "",
  });

  const { data, isPending } = useQuery<{
    dataPendaftaran: DataPendaftaran;
    dataSkl: DataBerkas;
    verifikatorKe: number;
  }>({
    queryKey: ["dataSkl"],
    queryFn: () => getDataVerifikasiSkl(nomorPendaftaranParams),
    enabled: !!nomorPendaftaranParams,
  });

  const { data: system } = useQuery({
    queryKey: ["masterSystem"],
    queryFn: getMasterSystem,
  });

  const isWaktuSanggahBerkas =
    system && system[0]?.is_waktu_sanggah_berkas_daftarkembali;

  const queryClient = useQueryClient();
  const {
    mutate: mutateValidasi,
    isPending: isPendingValidasi,
    error: errorValidasi,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataSkl"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    mutate: mutateResetValidasi,
    isPending: isPendingResetValidasi,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      setForm((prev) => ({ ...prev, catatan_verifikator: "" }));
      queryClient.invalidateQueries({
        queryKey: ["dataSkl"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleValidate = (status: boolean) => {
    mutateValidasi({
      ...form,
      id_data_pendaftaran: String(data?.dataPendaftaran?.id_data_pendaftaran),
      id_data_berkas_siswa: String(data?.dataSkl?.id_data_berkas_siswa),
      is_valid: status,
    });
  };

  const handleReset = () => {
    mutateResetValidasi({
      id_data_pendaftaran:
        String(data?.dataPendaftaran?.id_data_pendaftaran) || "",
      id_data_berkas_siswa: String(data?.dataSkl?.id_data_berkas_siswa) || "",
    });
  };

  useEffect(() => {
    if (!data) return;
    setForm((prev) => ({
      ...prev,
      catatan_verifikator: data?.dataSkl?.catatan_verifikator || "",
    }));
  }, [data]);

  const returnUrl = searchParams.get("returnTo")
    ? decodeURIComponent(searchParams.get("returnTo")!)
    : "/verifikator/skl/verifikasi-skl-peserta"; // Fallback URL jika diakses langsung

  if (isPending) {
    return (
      <div className="flex flex-col h-full min-h-screen bg-background dark:bg-[#09090b] p-4 md:p-6 transition-colors duration-200">
        {/* HEADER SKELETON */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {/* Back Button Skeleton */}
            <div className="h-10 w-10 rounded-lg bg-muted dark:bg-zinc-800 animate-pulse" />
            <div className="space-y-2">
              {/* Title Skeleton */}
              <div className="h-8 w-48 sm:w-64 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
              {/* Subtitle Skeleton */}
              <div className="h-4 w-64 sm:w-80 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
            </div>
          </div>
        </div>

        <section className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 xl:h-[calc(100vh-160px)]">
          {/* KOLOM KIRI SKELETON: PREVIEW DOKUMEN (Span 7) */}
          <div className="xl:col-span-7 flex flex-col h-[70vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card shadow-sm relative">
            <div className="p-4 border-b border-border dark:border-zinc-800 bg-muted/20 flex items-center gap-2">
              <div className="h-8 w-8 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
              <div className="h-6 w-1/3 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
            </div>
            {/* Iframe Placeholder */}
            <div className="flex-1 bg-muted/10 dark:bg-zinc-900/50 animate-pulse flex items-center justify-center">
              <svg
                className="w-16 h-16 text-muted dark:text-zinc-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>

          {/* KOLOM KANAN SKELETON: BIODATA & PANEL (Span 5) */}
          <div className="xl:col-span-5 flex flex-col gap-6 h-full">
            {/* Card Biodata Skeleton */}
            <div className="border border-border dark:border-zinc-800 rounded-xl bg-card shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border dark:border-zinc-800 bg-muted/10">
                <div className="h-6 w-32 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
              </div>
              <div className="p-4 space-y-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex justify-between items-center">
                    <div className="h-4 w-24 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
                    <div className="h-4 w-40 bg-muted/60 dark:bg-zinc-800/60 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </div>

            {/* Panel Verifikasi Skeleton */}
            <div className="border border-border dark:border-zinc-800 rounded-xl bg-card shadow-sm p-5 space-y-4">
              <div className="h-5 w-32 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
              <div className="h-24 w-full bg-muted/30 dark:bg-zinc-900/50 rounded-lg animate-pulse" />
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="h-12 bg-muted dark:bg-zinc-800 rounded-lg animate-pulse" />
                <div className="h-12 bg-muted dark:bg-zinc-800 rounded-lg animate-pulse" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-background dark:bg-[#09090b] text-foreground transition-colors duration-200 p-4 md:p-6">
      {/* HEADER PAGE & TOMBOL KEMBALI */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={returnUrl}
            className="inline-flex items-center justify-center rounded-lg border border-border bg-card dark:border-zinc-800 dark:bg-zinc-900 hover:bg-muted dark:hover:bg-zinc-800 text-muted-foreground hover:text-foreground h-10 w-10 transition-colors shadow-sm"
            title="Kembali"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Detail Verifikasi SKL
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Periksa kecocokan data biodata dengan dokumen Surat Keterangan
              Lulus.
            </p>
          </div>
        </div>
      </div>

      <section className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 xl:h-[calc(100vh-160px)]">
        {/* KOLOM KIRI: PREVIEW DOKUMEN (Span 7) */}
        <div className="xl:col-span-7 flex flex-col h-[70vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card shadow-sm relative">
          <div className="z-10 p-4 border-b border-border dark:border-zinc-800 bg-muted/30 dark:bg-zinc-900/50 backdrop-blur-md">
            <h1 className="font-bold text-lg sm:text-xl flex items-center gap-2">
              <span className="p-1.5 bg-primary/10 rounded text-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
              {data?.dataSkl?.sys_berkas?.jenis_berkas?.nama_jenis_berkas ||
                "Preview Berkas"}
              {isWaktuSanggahBerkas && (
                <Badge variant="destructive" className="ml-2 animate-pulse">
                  Mode Sanggah
                </Badge>
              )}
            </h1>
          </div>
          <div className="flex-1 relative bg-zinc-100 dark:bg-zinc-900">
            <iframe
              src={
                isWaktuSanggahBerkas
                  ? data?.dataSkl?.path_sanggah_berkas
                  : data?.dataSkl?.path_berkas
              }
              title="Preview Berkas"
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>

        {/* KOLOM KANAN: BIODATA & VERIFIKASI (Span 5) */}
        <div className="xl:col-span-5 flex flex-col gap-6 overflow-y-auto pr-1 custom-scrollbar h-full">
          {/* CARD BIODATA */}
          <Card className="border-border dark:border-zinc-800 bg-card shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border dark:border-zinc-800 bg-muted/20">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Biodata Peserta
              </h2>
            </div>
            <div className="p-0">
              <div className="divide-y divide-border dark:divide-zinc-800">
                {[
                  {
                    label: "Nama Lengkap",
                    value: data?.dataPendaftaran?.data_siswa?.nama_lengkap,
                  },
                  {
                    label: "Jenis Kelamin",
                    value:
                      data?.dataPendaftaran?.data_siswa?.jenis_kelamin === "P"
                        ? "Perempuan"
                        : data?.dataPendaftaran?.data_siswa?.jenis_kelamin ===
                            "L"
                          ? "Laki-Laki"
                          : "-",
                  },
                  {
                    label: "Nomor Telepon",
                    value: data?.dataPendaftaran?.data_siswa?.handphone,
                  },
                  {
                    label: "Tinggi / Berat",
                    value: `${data?.dataPendaftaran?.data_siswa?.tinggi_badan ?? 0} cm / ${data?.dataPendaftaran?.data_siswa?.berat_badan ?? 0} kg`,
                  },
                  {
                    label: "Sekolah Asal",
                    value:
                      data?.dataPendaftaran?.data_siswa?.sekolah?.nama_sekolah,
                  },
                  {
                    label: "No. Pendaftaran",
                    value: (
                      <span className="font-mono text-primary font-semibold">
                        {data?.dataPendaftaran?.nomor_pendaftaran}
                      </span>
                    ),
                  },
                  {
                    label: "Program Studi",
                    value: data?.dataPendaftaran?.unitkerja?.nama,
                  },
                  {
                    label: "Status Validasi",
                    value: (
                      <Badge
                        variant="outline"
                        className={
                          data?.dataSkl?.is_valid === null
                            ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                            : data?.dataSkl?.is_valid
                              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                              : "bg-destructive/10 text-destructive border-destructive/20"
                        }
                      >
                        {data?.dataSkl?.is_valid === null
                          ? "Menunggu Validasi"
                          : data?.dataSkl?.is_valid
                            ? "Valid"
                            : "Tidak Valid"}
                      </Badge>
                    ),
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex flex-col sm:flex-row sm:items-center py-3 px-4 hover:bg-muted/30 transition-colors"
                  >
                    <span className="text-sm text-muted-foreground sm:w-1/3 mb-1 sm:mb-0">
                      {item.label}
                    </span>
                    <span className="text-sm font-medium sm:w-2/3">
                      {item.value || "-"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* CARD PANEL VERIFIKATOR */}
          <Card className="border-border dark:border-zinc-800 bg-card shadow-sm border-t-4 border-t-primary">
            <div className="p-5 space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-bold flex items-center gap-2 text-foreground">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Catatan Verifikasi
                </Label>
                <Textarea
                  placeholder="Berikan alasan jika berkas tidak valid..."
                  className="min-h-[100px] bg-muted/20 focus-visible:ring-primary/50 dark:border-zinc-700 resize-none text-foreground"
                  value={form.catatan_verifikator}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      catatan_verifikator: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={errorValidasi?.response?.data.errors?.catatan_verifikator?.join(
                    ", ",
                  )}
                />
              </div>

              <div className="pt-2">
                {data?.dataSkl?.is_valid !== null ? (
                  <div className="flex flex-col gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">
                        Hasil validasi anda:
                      </span>
                      <Badge
                        variant={
                          data?.dataSkl?.is_valid ? "default" : "destructive"
                        }
                        className="px-3 py-1 font-bold shadow-sm"
                      >
                        {data?.dataSkl?.is_valid ? "VALID" : "TIDAK VALID"}
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-10 border-dashed dark:border-zinc-700 bg-background hover:bg-muted"
                      onClick={handleReset}
                      disabled={isPendingResetValidasi}
                    >
                      {isPendingResetValidasi ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <GiBackwardTime className="mr-2 h-4 w-4" /> Reset
                          Keputusan
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      variant="outline"
                      className="h-12 border-destructive/30 text-destructive hover:bg-destructive hover:text-white transition-all shadow-sm font-semibold"
                      onClick={() => handleValidate(false)}
                      disabled={isPendingValidasi}
                    >
                      {isPendingValidasi ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <MdBlockFlipped className="mr-2 h-5 w-5" /> Tidak
                          Valid
                        </>
                      )}
                    </Button>
                    <Button
                      className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white transition-all shadow-md font-semibold"
                      onClick={() => handleValidate(true)}
                      disabled={isPendingValidasi}
                    >
                      {isPendingValidasi ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FaCheck className="mr-2 h-4 w-4" /> Tandai Valid
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
